import type { ReactNode } from 'react'
import { Map as MapIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { PlaybackRoute, RouteMap } from '@/types'
import { formatMapId } from '@/common/routeUtil'
import { ENABLE_CARGO_TOGGLE, SLOT_TYPE_CARGO } from '@/config/scenarioConfig'

interface RouteListProps {
  map: RouteMap | null
  routes: PlaybackRoute[]
  thumbnails: Map<string, string>
  isSelected: (route: PlaybackRoute) => boolean
  onSelectRoute: (
    route: PlaybackRoute,
    index: number,
    mods: { ctrl: boolean; shift: boolean }
  ) => void
  renderUsageBadge?: (route: PlaybackRoute) => ReactNode
  // 行の右端に表示するアクション（ホバー時に表示。編集ボタンなど）
  renderActions?: (route: PlaybackRoute, index: number) => ReactNode
  // trueを返したルートは淡色表示になり選択できない
  isRouteDisabled?: (route: PlaybackRoute) => boolean
}

// プレイバックルートの一覧（コンパクトなリスト行）。
// 参照タブと編集時のルート選択で共通のデザインを使う。
export default function RouteList({
  map,
  routes,
  thumbnails,
  isSelected,
  onSelectRoute,
  renderUsageBadge,
  renderActions,
  isRouteDisabled,
}: RouteListProps) {
  const { t } = useTranslation()

  if (routes.length === 0) {
    return (
      <div className="text-gray-400 text-center py-6 text-xs">
        {t('scenarios.noRoutes')}
      </div>
    )
  }

  return (
    <ul className="flex flex-col gap-1 select-none">
      {routes.map((route, index) => {
        const formatted = formatMapId(route.mapId)
        const [datePart, timePart] = formatted.split(' ')
        const slot0 = map?.slots.get(route.slots[0])
        const slot1 = map?.slots.get(route.slots[1])
        const slot0IsCargo = slot0?.type === SLOT_TYPE_CARGO
        const slot1IsCargo = slot1?.type === SLOT_TYPE_CARGO
        const hasCargo = slot0IsCargo || slot1IsCargo
        // 高さ保存スロットのフォーク高（m → mm）
        const cargoHeights = [
          slot0IsCargo ? slot0!.height : null,
          slot1IsCargo ? slot1!.height : null,
        ].filter((h): h is number => h !== null)
        const selected = isSelected(route)
        const disabled = isRouteDisabled?.(route) ?? false
        const thumbnail = thumbnails.get(route.slots[0])
        const usageBadge = renderUsageBadge?.(route)
        const actions = renderActions?.(route, index)

        return (
          <li
            key={route.id}
            onClick={(e) => {
              if (disabled) return
              onSelectRoute(route, index, {
                ctrl: e.ctrlKey || e.metaKey,
                shift: e.shiftKey,
              })
            }}
            className={`group flex items-center gap-2 p-1.5 rounded-lg border transition-colors ${
              disabled
                ? 'bg-white border-gray-200 opacity-40 cursor-not-allowed'
                : selected
                  ? 'bg-orange-50 border-orange-400 cursor-pointer'
                  : 'bg-white border-gray-200 hover:bg-gray-50 cursor-pointer'
            }`}
          >
            <div className="h-10 w-12 flex-shrink-0 border border-gray-200 rounded bg-gray-100 overflow-hidden flex items-center justify-center">
              {thumbnail ? (
                <img
                  src={thumbnail}
                  alt={route.id}
                  className="w-full h-full object-contain"
                  draggable={false}
                />
              ) : (
                <MapIcon size={14} className="text-gray-300" />
              )}
            </div>
            <div className="flex-1 min-w-0 text-xs">
              <div className="flex items-center gap-1">
                {ENABLE_CARGO_TOGGLE && hasCargo && (
                  <span className="px-1 py-0.5 text-[10px] font-bold text-white bg-orange-500 rounded leading-none">
                    高
                  </span>
                )}
                <span className="font-medium text-gray-700">
                  {datePart} <span className="text-gray-500">{timePart}</span>
                </span>
              </div>
              {ENABLE_CARGO_TOGGLE && cargoHeights.length > 0 && (
                <div className="text-[11px] text-orange-600 mt-0.5">
                  {cargoHeights
                    .map((h) => `${Math.floor(h * 1000).toLocaleString()}mm`)
                    .join(' / ')}
                </div>
              )}
            </div>
            {usageBadge}
            {actions && (
              <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                {actions}
              </div>
            )}
          </li>
        )
      })}
    </ul>
  )
}
